import React, { useRef, useState } from "react";
import { API_BASE } from "../api/apiConfig";

function HomeBannerAbout({ onClose }) {
  
  const scrollRef = useRef(null);

  const [progress, setProgress] = useState(0);
  const [activeSection, setActiveSection] = useState("Story");
  const [isOpen, setIsOpen] = useState(true);

  const sections = ["Story", "Belief", "Trips", "Vision"];

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    const scrollTop = el.scrollTop;
    const scrollHeight = el.scrollHeight - el.clientHeight;
    const percent = (scrollTop / scrollHeight) * 100;
    setProgress(percent);

    sections.forEach((section) => {
      const element = document.getElementById(section);
      if (element) {
        const offsetTop = element.offsetTop;
        const offsetHeight = element.offsetHeight;

        if (
          scrollTop >= offsetTop - 120 &&
          scrollTop < offsetTop + offsetHeight - 120
        ) {
          setActiveSection(section);
        }
      }
    });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300"
        onClick={() => setIsOpen(false)}
      />

      {/* Bottom Sheet */}
      <div className="absolute bottom-0 left-0 right-0 bg-background rounded-t-3xl max-h-[85vh] overflow-hidden transition-transform duration-300 ease-out shadow-2xl translate-y-0" >

        {/* Header */}
        <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-xl border-b border-border/50">
          
          {/* Drag Indicator */}
          <div className="flex items-center justify-center pt-2.5 pb-1">
            <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
          </div>

          {/* Title + Close */}
          <div className="flex items-center justify-between px-4 pb-1.5">
            <h2 className="text-sm font-bold text-foreground">
              About Connecting Traveller
            </h2>

            <button
              onClick={onClose}
              className="w-7 h-7 rounded-full bg-muted flex items-center justify-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-3.5 w-3.5 text-muted-foreground"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 px-4 pb-2">
            {sections.map((section) => (
              <button
                key={section}
                onClick={() => scrollToSection(section)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                  activeSection === section
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {section}
              </button>
            ))}
          </div>

          {/* Progress Line */}
          <div className="h-[2px] bg-muted">
            <div
              className="h-full bg-primary transition-all duration-150 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Scrollable Content */}
        <div ref={scrollRef} onScroll={handleScroll} className="overflow-y-auto max-h-[calc(85vh-90px)] scroll-smooth">

<div className="relative h-[80px] overflow-hidden">
                    <img
                      alt="Travelers connecting at a scenic viewpoint"
                      loading="lazy"
                      decoding="async"
                      data-nimg="fill"
                      className="object-cover object-center"
                      style={{
                        position: "absolute",
                        height: "100%",
                        width: "100%",
                        inset: 0,
                        color: "transparent"
                      }}
                      src="assets/images/hero.jpg"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/60 to-slate-950/40" />
                    <div className="absolute inset-0 flex items-center px-4">
                      <div>
                        <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/20 backdrop-blur-sm border border-primary/30 mb-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-sparkles h-2.5 w-2.5 text-primary"
                          >
                            <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
                            <path d="M5 3v4" />
                            <path d="M19 17v4" />
                            <path d="M3 5h4" />
                            <path d="M17 19h4" />
                          </svg>
                          <span className="text-[10px] font-semibold text-primary">
                            Our Story
                          </span>
                        </div>
                        <h2 className="text-sm font-bold text-white leading-tight">
                          Travel Should Do More Than{" "}
                          <span className="text-primary">Entertain You</span>
                        </h2>
                      </div>
                    </div>
                  </div>
              <div className="px-4 pt-3 pb-8 space-y-5">
                <section id="Story">
                  
                  


                  <section className="transition-all duration-500 opacity-100 translate-y-0">
                    <p className="text-primary text-xs font-semibold tracking-wider uppercase mb-1">
                      Why We Exist
                    </p>
                    <h3 className="text-base font-bold text-foreground mb-1.5 leading-tight">
                      Very few experiences actually change your life. We're building one
                      that does.
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-3">
                      Connecting Traveller was born from the idea that travel should be
                      more than sightseeing. We saw too many people returning from trips
                      with photos but without real stories, skills, or friendships. Travel
                      should:
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex items-center gap-2 bg-primary/5 border border-primary/10 rounded-lg px-2.5 py-2">
                        <div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-zap h-3.5 w-3.5 text-primary"
                          >
                            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                          </svg>
                        </div>
                        <span className="text-foreground text-xs font-medium leading-tight">
                          Add skills to your life
                        </span>
                      </div>
                      <div className="flex items-center gap-2 bg-primary/5 border border-primary/10 rounded-lg px-2.5 py-2">
                        <div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-users h-3.5 w-3.5 text-primary"
                          >
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                            <circle cx={9} cy={7} r={4} />
                            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                          </svg>
                        </div>
                        <span className="text-foreground text-xs font-medium leading-tight">
                          Expand your world
                        </span>
                      </div>
                      <div className="flex items-center gap-2 bg-primary/5 border border-primary/10 rounded-lg px-2.5 py-2">
                        <div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-trending-up h-3.5 w-3.5 text-primary"
                          >
                            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                            <polyline points="16 7 22 7 22 13" />
                          </svg>
                        </div>
                        <span className="text-foreground text-xs font-medium leading-tight">
                          Help you grow
                        </span>
                      </div>
                      <div className="flex items-center gap-2 bg-primary/5 border border-primary/10 rounded-lg px-2.5 py-2">
                        <div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-sparkles h-3.5 w-3.5 text-primary"
                          >
                            <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
                            <path d="M5 3v4" />
                            <path d="M19 17v4" />
                            <path d="M3 5h4" />
                            <path d="M17 19h4" />
                          </svg>
                        </div>
                        <span className="text-foreground text-xs font-medium leading-tight">
                          Give you momentum
                        </span>
                      </div>
                    </div>
                  </section>
                  <section className="transition-all duration-500 opacity-100 translate-y-0 mt-5">
                    <h3 className="text-base font-bold text-foreground mb-2 leading-tight">
                      Most travel looks like this...
                    </h3>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="space-y-1.5">
                        <p className="text-xs font-semibold text-red-500 uppercase tracking-wider">
                          Typical trip
                        </p>
                        <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg px-2.5 py-2">
                          <div className="w-6 h-6 rounded bg-red-100 flex items-center justify-center flex-shrink-0">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={24}
                              height={24}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-map-pin h-3 w-3 text-red-600"
                            >
                              <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                              <circle cx={12} cy={10} r={3} />
                            </svg>
                          </div>
                          <span className="text-red-900/80 text-xs font-medium">
                            Rushed sightseeing
                          </span>
                        </div>
                        <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg px-2.5 py-2">
                          <div className="w-6 h-6 rounded bg-red-100 flex items-center justify-center flex-shrink-0">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={24}
                              height={24}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-camera h-3 w-3 text-red-600"
                            >
                              <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                              <circle cx={12} cy={13} r={3} />
                            </svg>
                          </div>
                          <span className="text-red-900/80 text-xs font-medium">
                            Photos, no real stories
                          </span>
                        </div>
                        <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg px-2.5 py-2">
                          <div className="w-6 h-6 rounded bg-red-100 flex items-center justify-center flex-shrink-0">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={24}
                              height={24}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-clock h-3 w-3 text-red-600"
                            >
                              <circle cx={12} cy={12} r={10} />
                              <polyline points="12 6 12 12 16 14" />
                            </svg>
                          </div>
                          <span className="text-red-900/80 text-xs font-medium">
                            Refreshed but unchanged
                          </span>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">
                          CT experience
                        </p>
                        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-lg px-2.5 py-2">
                          <div className="w-6 h-6 rounded bg-emerald-100 flex items-center justify-center flex-shrink-0">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={24}
                              height={24}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-graduation-cap h-3 w-3 text-emerald-700"
                            >
                              <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                              <path d="M6 12v5c3 3 9 3 12 0v-5" />
                            </svg>
                          </div>
                          <span className="text-emerald-900/80 text-xs font-medium">
                            Learn real skills
                          </span>
                        </div>
                        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-lg px-2.5 py-2">
                          <div className="w-6 h-6 rounded bg-emerald-100 flex items-center justify-center flex-shrink-0">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={24}
                              height={24}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-users h-3 w-3 text-emerald-700"
                            >
                              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                              <circle cx={9} cy={7} r={4} />
                              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                          </div>
                          <span className="text-emerald-900/80 text-xs font-medium">
                            Meet great people
                          </span>
                        </div>
                        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-lg px-2.5 py-2">
                          <div className="w-6 h-6 rounded bg-emerald-100 flex items-center justify-center flex-shrink-0">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={24}
                              height={24}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-heart h-3 w-3 text-emerald-700"
                            >
                              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                            </svg>
                          </div>
                          <span className="text-emerald-900/80 text-xs font-medium">
                            Real momentum
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-3 border border-slate-200 flex items-start gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-quote h-4 w-4 text-primary/40 flex-shrink-0 mt-0.5"
                      >
                        <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" />
                        <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z" />
                      </svg>
                      <p className="text-foreground text-sm italic leading-relaxed">
                        “What if travel could move your life forward?” — That question
                        became Connecting Traveller.
                      </p>
                    </div>
                  </section>
                  <div className="relative aspect-[2.5/1] rounded-xl overflow-hidden mt-5">
                    <img
                      alt="Travelers around a bonfire"
                      loading="lazy"
                      decoding="async"
                      data-nimg="fill"
                      className="object-cover"
                      style={{
                        position: "absolute",
                        height: "100%",
                        width: "100%",
                        inset: 0,
                        color: "transparent"
                      }}
                      src="/assets/images/community.jpg"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-transparent" />
                    <p className="absolute bottom-2.5 left-3 text-white/90 text-xs font-medium italic">
                      Real connections start here.
                    </p>
                  </div>
                </section>

                <section id="Belief">
                  <section className="transition-all duration-500 opacity-100 translate-y-0">
                    <div className="bg-slate-900 rounded-2xl p-4">
                      <p className="text-primary text-xs font-semibold tracking-wider uppercase mb-1">
                        Our Belief
                      </p>
                      <h3 className="text-base font-bold text-white mb-1.5 leading-tight text-balance">
                        Travel is the best classroom in the world.
                      </h3>
                      <p className="text-white/60 text-sm leading-relaxed mb-3">
                        You learn more in a new environment, surrounded by diverse people,
                        experiencing real situations — not theories. That's why we blend:
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-white/5 border border-white/10 rounded-xl p-2.5 text-center">
                          <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center mx-auto mb-1.5">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={24}
                              height={24}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-compass h-4 w-4 text-amber-400"
                            >
                              <circle cx={12} cy={12} r={10} />
                              <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
                            </svg>
                          </div>
                          <p className="text-white text-xs font-bold leading-tight">
                            Travel With Purpose
                          </p>
                          <p className="text-white/50 text-[11px] mt-0.5 leading-tight">
                            Every journey should add value
                          </p>
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded-xl p-2.5 text-center">
                          <div className="w-8 h-8 rounded-lg bg-sky-500/20 flex items-center justify-center mx-auto mb-1.5">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={24}
                              height={24}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-book-open h-4 w-4 text-sky-400"
                            >
                              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                            </svg>
                          </div>
                          <p className="text-white text-xs font-bold leading-tight">
                            Learn By Living
                          </p>
                          <p className="text-white/50 text-[11px] mt-0.5 leading-tight">
                            Best learning is outside classrooms
                          </p>
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded-xl p-2.5 text-center">
                          <div className="w-8 h-8 rounded-lg bg-rose-500/20 flex items-center justify-center mx-auto mb-1.5">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={24}
                              height={24}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-heart h-4 w-4 text-rose-400"
                            >
                              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                            </svg>
                          </div>
                          <p className="text-white text-xs font-bold leading-tight">
                            People Matter
                          </p>
                          <p className="text-white/50 text-[11px] mt-0.5 leading-tight">
                            People make the journey
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>
                  <div className="relative aspect-[2.5/1] rounded-xl overflow-hidden mt-5">
                    <img
                      alt="Outdoor workshop on a hillside"
                      loading="lazy"
                      decoding="async"
                      data-nimg="fill"
                      className="object-cover"
                      style={{
                        position: "absolute",
                        height: "100%",
                        width: "100%",
                        inset: 0,
                        color: "transparent"
                      }}
                      src="/assets/images/learning.jpg"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
                    <p className="absolute bottom-2.5 left-3 text-white font-bold text-sm">
                      Learning should feel like living.
                    </p>
                  </div>
                  <section className="transition-all duration-500 opacity-100 translate-y-0 mt-5">
                    <p className="text-muted-foreground text-sm leading-relaxed mb-2.5">
                      Learning happens through conversations, workshops in inspiring
                      locations, and collaboration with fellow travellers. No pressure. No
                      exams. Just natural growth.
                    </p>
                    <div className="space-y-1.5 mb-4">
                      <div className="flex items-start gap-2.5 bg-sky-50 border border-sky-100 rounded-xl px-3 py-2.5">
                        <div className="w-7 h-7 rounded-lg bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-message-circle h-3.5 w-3.5 text-sky-700"
                          >
                            <path d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-foreground text-xs font-bold">
                            Conversations
                          </p>
                          <p className="text-muted-foreground text-[11px] leading-relaxed">
                            Ideas flow naturally in new environments with diverse minds.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2.5 bg-sky-50 border border-sky-100 rounded-xl px-3 py-2.5">
                        <div className="w-7 h-7 rounded-lg bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-sparkles h-3.5 w-3.5 text-sky-700"
                          >
                            <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
                            <path d="M5 3v4" />
                            <path d="M19 17v4" />
                            <path d="M3 5h4" />
                            <path d="M17 19h4" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-foreground text-xs font-bold">Workshops</p>
                          <p className="text-muted-foreground text-[11px] leading-relaxed">
                            Hands-on sessions in inspiring locations — AI, photography,
                            wellness.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2.5 bg-sky-50 border border-sky-100 rounded-xl px-3 py-2.5">
                        <div className="w-7 h-7 rounded-lg bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-users h-3.5 w-3.5 text-sky-700"
                          >
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                            <circle cx={9} cy={7} r={4} />
                            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-foreground text-xs font-bold">
                            Collaboration
                          </p>
                          <p className="text-muted-foreground text-[11px] leading-relaxed">
                            Learn from fellow travellers with different skills and
                            perspectives.
                          </p>
                        </div>
                      </div>
                    </div>
                    <p className="text-primary text-xs font-semibold tracking-wider uppercase mb-1">
                      Community First
                    </p>
                    <h3 className="text-base font-bold text-foreground mb-1.5">
                      People matter more than destinations.
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-2">
                      We curate groups so you travel with curious, growth-oriented,
                      supportive people. The connections you make aren't just for the trip
                      — they're for life.
                    </p>
                    <div className="flex gap-1.5 flex-wrap mb-2">
                      <span className="px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 text-[11px] font-semibold">
                        Curious Minds
                      </span>
                      <span className="px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 text-[11px] font-semibold">
                        Growth-Oriented
                      </span>
                      <span className="px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 text-[11px] font-semibold">
                        Supportive
                      </span>
                      <span className="px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 text-[11px] font-semibold">
                        Open-Hearted
                      </span>
                      <span className="px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 text-[11px] font-semibold">
                        Creative Thinkers
                      </span>
                    </div>
                    <div className="bg-rose-50 border border-rose-100 rounded-xl p-3 flex items-center gap-2.5">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-heart h-4 w-4 text-rose-500 flex-shrink-0"
                      >
                        <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                      </svg>
                      <p className="text-rose-900/80 text-xs">
                        <span className="font-bold">You don't feel like a tourist.</span>{" "}
                        You feel like you belong.
                      </p>
                    </div>
                  </section>
                  <section className="transition-all duration-500 opacity-100 translate-y-0 mt-5">
                    <p className="text-primary text-xs font-semibold tracking-wider uppercase mb-1">
                      Curated, Not Mass
                    </p>
                    <h3 className="text-base font-bold text-foreground mb-1.5">
                      Every trip is designed with intention.
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-2.5">
                      Smaller groups mean deeper conversations, better learning, stronger
                      bonds, and more meaningful experiences.
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-amber-50 border border-amber-100 rounded-xl p-2.5 text-center">
                        <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center mx-auto mb-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-message-circle h-3.5 w-3.5 text-amber-700"
                          >
                            <path d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z" />
                          </svg>
                        </div>
                        <p className="text-foreground text-xs font-semibold">
                          Deeper conversations
                        </p>
                        <p className="text-muted-foreground text-[11px] mt-0.5">
                          Real dialogue, not chatter.
                        </p>
                      </div>
                      <div className="bg-amber-50 border border-amber-100 rounded-xl p-2.5 text-center">
                        <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center mx-auto mb-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-book-open h-3.5 w-3.5 text-amber-700"
                          >
                            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                          </svg>
                        </div>
                        <p className="text-foreground text-xs font-semibold">
                          Better learning
                        </p>
                        <p className="text-muted-foreground text-[11px] mt-0.5">
                          More attention, more growth.
                        </p>
                      </div>
                      <div className="bg-amber-50 border border-amber-100 rounded-xl p-2.5 text-center">
                        <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center mx-auto mb-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-heart h-3.5 w-3.5 text-amber-700"
                          >
                            <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                          </svg>
                        </div>
                        <p className="text-foreground text-xs font-semibold">
                          Stronger bonds
                        </p>
                        <p className="text-muted-foreground text-[11px] mt-0.5">
                          Lasting relationships.
                        </p>
                      </div>
                      <div className="bg-amber-50 border border-amber-100 rounded-xl p-2.5 text-center">
                        <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center mx-auto mb-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-sparkles h-3.5 w-3.5 text-amber-700"
                          >
                            <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
                            <path d="M5 3v4" />
                            <path d="M19 17v4" />
                            <path d="M3 5h4" />
                            <path d="M17 19h4" />
                          </svg>
                        </div>
                        <p className="text-foreground text-xs font-semibold">
                          Meaningful moments
                        </p>
                        <p className="text-muted-foreground text-[11px] mt-0.5">
                          Every moment with intention.
                        </p>
                      </div>
                    </div>
                  </section>
                  <div className="relative aspect-[2.5/1] rounded-xl overflow-hidden mt-5">
                    <img
                      alt="Traveler learning photography"
                      loading="lazy"
                      decoding="async"
                      data-nimg="fill"
                      className="object-cover"
                      style={{
                        position: "absolute",
                        height: "100%",
                        width: "100%",
                        inset: 0,
                        color: "transparent"
                      }}
                      src="/assets/images/skills.jpg"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-transparent" />
                    <p className="absolute bottom-2.5 left-3 text-white/90 text-xs font-medium italic">
                      Learn by doing, in places that inspire.
                    </p>
                  </div>
                </section>

                <section id="Trips">
                  <section className="transition-all duration-500 opacity-100 translate-y-0 ">
                    <div className="bg-slate-900 rounded-2xl p-4">
                      <div className="flex items-center gap-1.5 mb-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={24}
                          height={24}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-compass h-4 w-4 text-primary"
                        >
                          <circle cx={12} cy={12} r={10} />
                          <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
                        </svg>
                        <p className="text-primary text-xs font-semibold tracking-wider uppercase">
                          Experience Trips
                        </p>
                      </div>
                      <h3 className="text-base font-bold text-white mb-1.5 leading-tight">
                        What are Experience Trips?
                      </h3>
                      <p className="text-white/60 text-sm leading-relaxed mb-3">
                        Curated journeys combining travel with skill-building, mentorship,
                        and community. Come back better, not just rested.
                      </p>
                      <p className="text-primary text-xs font-semibold tracking-wider uppercase mb-2">
                        How It Works
                      </p>
                      <div className="space-y-1.5 mb-4">
                        <div className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-3">
                          <span className="text-lg font-bold text-primary/40 flex-shrink-0 w-7">
                            01
                          </span>
                          <div>
                            <p className="text-white text-xs font-bold">
                              Choose Your Journey
                            </p>
                            <p className="text-white/50 text-[11px] leading-relaxed">
                              Pick a destination + theme that resonates with you
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-3">
                          <span className="text-lg font-bold text-primary/40 flex-shrink-0 w-7">
                            02
                          </span>
                          <div>
                            <p className="text-white text-xs font-bold">
                              Travel With Your Tribe
                            </p>
                            <p className="text-white/50 text-[11px] leading-relaxed">
                              Join 10-15 growth-minded travellers
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-3">
                          <span className="text-lg font-bold text-primary/40 flex-shrink-0 w-7">
                            03
                          </span>
                          <div>
                            <p className="text-white text-xs font-bold">
                              Learn Through Living
                            </p>
                            <p className="text-white/50 text-[11px] leading-relaxed">
                              Workshops, projects &amp; real skill-building
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-3">
                          <span className="text-lg font-bold text-primary/40 flex-shrink-0 w-7">
                            04
                          </span>
                          <div>
                            <p className="text-white text-xs font-bold">
                              Return Transformed
                            </p>
                            <p className="text-white/50 text-[11px] leading-relaxed">
                              New skills, connections &amp; perspectives
                            </p>
                          </div>
                        </div>
                      </div>
                      <p className="text-primary text-xs font-semibold tracking-wider uppercase mb-2">
                        What You Gain
                      </p>
                      <div className="space-y-1.5 mb-4">
                        <div className="flex items-start gap-2.5 bg-white/5 border border-white/10 rounded-xl p-3">
                          <div className="w-7 h-7 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={24}
                              height={24}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-graduation-cap h-3.5 w-3.5 text-primary"
                            >
                              <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                              <path d="M6 12v5c3 3 9 3 12 0v-5" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-white text-xs font-bold">
                              Skill Acquisition
                            </p>
                            <p className="text-white/50 text-[11px] leading-relaxed">
                              Master practical skills like AI, photography, culinary arts
                              while exploring incredible destinations.
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2.5 bg-white/5 border border-white/10 rounded-xl p-3">
                          <div className="w-7 h-7 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={24}
                              height={24}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-users h-3.5 w-3.5 text-primary"
                            >
                              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                              <circle cx={9} cy={7} r={4} />
                              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-white text-xs font-bold">
                              Network Expansion
                            </p>
                            <p className="text-white/50 text-[11px] leading-relaxed">
                              Travel with professionals, creators, entrepreneurs. Your
                              next collaborator could be on the same trip.
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2.5 bg-white/5 border border-white/10 rounded-xl p-3">
                          <div className="w-7 h-7 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={24}
                              height={24}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-trending-up h-3.5 w-3.5 text-primary"
                            >
                              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                              <polyline points="16 7 22 7 22 13" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-white text-xs font-bold">
                              Personal Growth
                            </p>
                            <p className="text-white/50 text-[11px] leading-relaxed">
                              Step outside your comfort zone in a supportive environment.
                              Build confidence and new perspectives.
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2.5 bg-white/5 border border-white/10 rounded-xl p-3">
                          <div className="w-7 h-7 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={24}
                              height={24}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-lightbulb h-3.5 w-3.5 text-primary"
                            >
                              <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
                              <path d="M9 18h6" />
                              <path d="M10 22h4" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-white text-xs font-bold">
                              Fresh Perspectives
                            </p>
                            <p className="text-white/50 text-[11px] leading-relaxed">
                              New environments spark new ideas. Travel + learning creates
                              breakthroughs you can't get in a classroom.
                            </p>
                          </div>
                        </div>
                      </div>
                      <p className="text-primary text-xs font-semibold tracking-wider uppercase mb-2">
                        6 Reasons To Go
                      </p>
                      <div className="grid grid-cols-2 gap-1.5">
                        <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-2.5 py-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-zap h-3.5 w-3.5 text-emerald-400 flex-shrink-0"
                          >
                            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                          </svg>
                          <span className="text-white/80 text-xs font-medium leading-tight">
                            Add tangible skills to your life
                          </span>
                        </div>
                        <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-2.5 py-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-globe h-3.5 w-3.5 text-emerald-400 flex-shrink-0"
                          >
                            <circle cx={12} cy={12} r={10} />
                            <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
                            <path d="M2 12h20" />
                          </svg>
                          <span className="text-white/80 text-xs font-medium leading-tight">
                            See the world while growing
                          </span>
                        </div>
                        <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-2.5 py-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-users h-3.5 w-3.5 text-emerald-400 flex-shrink-0"
                          >
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                            <circle cx={9} cy={7} r={4} />
                            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                          </svg>
                          <span className="text-white/80 text-xs font-medium leading-tight">
                            Build a network that grows
                          </span>
                        </div>
                        <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-2.5 py-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-shield h-3.5 w-3.5 text-emerald-400 flex-shrink-0"
                          >
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
                          </svg>
                          <span className="text-white/80 text-xs font-medium leading-tight">
                            Safe, curated groups
                          </span>
                        </div>
                        <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-2.5 py-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-star h-3.5 w-3.5 text-emerald-400 flex-shrink-0"
                          >
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                          </svg>
                          <span className="text-white/80 text-xs font-medium leading-tight">
                            Expert-led sessions
                          </span>
                        </div>
                        <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-2.5 py-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-compass h-3.5 w-3.5 text-emerald-400 flex-shrink-0"
                          >
                            <circle cx={12} cy={12} r={10} />
                            <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
                          </svg>
                          <span className="text-white/80 text-xs font-medium leading-tight">
                            Adventure + meaningful learning
                          </span>
                        </div>
                      </div>
                    </div>
                  </section>
                </section>

                <section id="Vision">
                  <section className="transition-all duration-500 opacity-100 translate-y-0 ">
                    <p className="text-primary text-xs font-semibold tracking-wider uppercase mb-1">
                      Why Now
                    </p>
                    <h3 className="text-base font-bold text-foreground mb-1.5 leading-tight">
                      Today's generation wants more.
                    </h3>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div className="bg-muted/50 rounded-xl p-3 border border-border">
                        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                          Not just
                        </p>
                        <p className="text-muted-foreground text-xs line-through decoration-muted-foreground/40 mb-0.5">
                          Breaks from work
                        </p>
                        <p className="text-muted-foreground text-xs line-through decoration-muted-foreground/40 mb-0.5">
                          Escape from routine
                        </p>
                        <p className="text-muted-foreground text-xs line-through decoration-muted-foreground/40 mb-0.5">
                          Tourist checklists
                        </p>
                      </div>
                      <div className="bg-primary/5 rounded-xl p-3 border border-primary/15">
                        <p className="text-[11px] font-semibold text-primary uppercase tracking-wider mb-1.5">
                          They want
                        </p>
                        <p className="text-foreground text-xs font-semibold mb-0.5">
                          Purpose
                        </p>
                        <p className="text-foreground text-xs font-semibold mb-0.5">
                          Growth
                        </p>
                        <p className="text-foreground text-xs font-semibold mb-0.5">
                          Connection
                        </p>
                        <p className="text-foreground text-xs font-semibold mb-0.5">
                          Aligned experiences
                        </p>
                      </div>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      CT is for people who don't want to pause life — they want to{" "}
                      <span className="text-foreground font-semibold">
                        build it while living it.
                      </span>
                    </p>
                  </section>
                  <section className="transition-all duration-500 opacity-100 translate-y-0 ">
                    <p className="text-primary text-xs font-semibold tracking-wider uppercase mb-1">
                      Is This You?
                    </p>
                    <h3 className="text-base font-bold text-foreground mb-2 leading-tight">
                      Who CT Is Really For
                    </h3>
                    <div className="space-y-1.5 mb-2">
                      <div className="bg-background border border-border rounded-xl px-3 py-2.5 flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-sparkles h-3.5 w-3.5 text-primary"
                          >
                            <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
                            <path d="M5 3v4" />
                            <path d="M19 17v4" />
                            <path d="M3 5h4" />
                            <path d="M17 19h4" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-foreground font-bold text-xs">
                            Curious travellers
                          </p>
                          <p className="text-muted-foreground text-[11px]">
                            Who want more than sightseeing
                          </p>
                        </div>
                      </div>
                      <div className="bg-background border border-border rounded-xl px-3 py-2.5 flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-lightbulb h-3.5 w-3.5 text-primary"
                          >
                            <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
                            <path d="M9 18h6" />
                            <path d="M10 22h4" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-foreground font-bold text-xs">
                            Professionals &amp; creators
                          </p>
                          <p className="text-muted-foreground text-[11px]">
                            Looking to upgrade skills while travelling
                          </p>
                        </div>
                      </div>
                      <div className="bg-background border border-border rounded-xl px-3 py-2.5 flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-users h-3.5 w-3.5 text-primary"
                          >
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                            <circle cx={9} cy={7} r={4} />
                            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-foreground font-bold text-xs">
                            First-time solo travellers
                          </p>
                          <p className="text-muted-foreground text-[11px]">
                            Who want safe, community-driven trips
                          </p>
                        </div>
                      </div>
                      <div className="bg-background border border-border rounded-xl px-3 py-2.5 flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-target h-3.5 w-3.5 text-primary"
                          >
                            <circle cx={12} cy={12} r={10} />
                            <circle cx={12} cy={12} r={6} />
                            <circle cx={12} cy={12} r={2} />
                          </svg>
                        </div>
                        <div>
                          <p className="text-foreground font-bold text-xs">
                            Growth seekers
                          </p>
                          <p className="text-muted-foreground text-[11px]">
                            Who believe travel should add value to life
                          </p>
                        </div>
                      </div>
                    </div>
                    <p className="text-muted-foreground text-xs text-center italic">
                      You just need the desire to grow.
                    </p>
                  </section>
                  <div className="relative aspect-[2.5/1] rounded-xl overflow-hidden">
                    <img
                      alt="Travelers hiking through a mountain trail"
                      loading="lazy"
                      decoding="async"
                      data-nimg="fill"
                      className="object-cover"
                      style={{
                        position: "absolute",
                        height: "100%",
                        width: "100%",
                        inset: 0,
                        color: "transparent"
                      }}
                      src="/assets/images/experience.jpg"
                    />
                  </div>
                  <section className="transition-all duration-500 opacity-100 translate-y-0 mt-5">
                    <div className="bg-slate-900 rounded-2xl p-4 text-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={24}
                        height={24}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-globe h-6 w-6 text-primary mx-auto mb-1.5"
                      >
                        <circle cx={12} cy={12} r={10} />
                        <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
                        <path d="M2 12h20" />
                      </svg>
                      <p className="text-primary text-xs font-semibold tracking-wider uppercase mb-1">
                        Our Vision
                      </p>
                      <h3 className="text-base font-bold text-white leading-snug text-balance mb-1.5">
                        A global community where people travel with intention, learn
                        through experiences, and{" "}
                        <span className="text-primary">
                          connect beyond superficial networking.
                        </span>
                      </h3>
                      <p className="text-white/50 text-xs">
                        More skilled. More confident. More aware.
                      </p>
                    </div>
                  </section>
                  <section className="transition-all duration-500 opacity-100 translate-y-0 mt-5">
                    <div className="bg-primary rounded-2xl p-4 text-center">
                      <h3 className="text-base font-bold text-primary-foreground mb-1 text-balance leading-tight">
                        You don't just explore destinations.
                      </h3>
                      <p className="text-primary-foreground/70 text-sm mb-1 leading-relaxed">
                        You explore ideas, skills, and possibilities. You return upgraded.
                      </p>
                      <p className="text-primary-foreground/50 text-xs mb-3">
                        This isn't just where you go. It's who you become along the way.
                      </p>
                      <div className="flex gap-2">
                        <a className="flex-1" href="/experience-trips">
                          <button className="inline-flex items-center justify-center whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 w-full bg-background hover:bg-background/90 text-primary py-3 rounded-xl text-xs font-semibold group shadow-lg">
                            Explore Experiences
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={24}
                              height={24}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-arrow-right ml-1.5 h-3.5 w-3.5 group-hover:translate-x-1 transition-transform"
                            >
                              <path d="M5 12h14" />
                              <path d="m12 5 7 7-7 7" />
                            </svg>
                          </button>
                        </a>
                        <a className="flex-1" href="/community">
                          <button className="inline-flex items-center justify-center whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 w-full bg-background hover:bg-background/90 text-primary py-3 rounded-xl text-xs font-semibold group shadow-lg">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={24}
                              height={24}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-users mr-1.5 h-3.5 w-3.5"
                            >
                              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                              <circle cx={9} cy={7} r={4} />
                              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                            Community
                          </button>
                        </a>
                      </div>
                    </div>
                  </section>
                </section>
              </div>

          
        </div>

      </div>
    </div>
  );
}

export default HomeBannerAbout;
